import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { Button } from "@/components/ui/button";
import { Copy, Share2, ExternalLink, Check, Link2, Download } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable widget for sharing a public tracking URL (no auth required).
 *
 * Props:
 *   qrCodeId   — string, the QR/tracking code. Required.
 *   variant    — "card" (default, full panel) | "inline" (compact icons only) | "icon" (single icon button)
 *   label      — optional label override for the card header
 */
export default function ShareTrackingLink({ qrCodeId, variant = "card", label }) {
  const [copied, setCopied] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState("");

  const url = qrCodeId ? `${window.location.origin}/track/${qrCodeId}` : "";
  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  // Generate a tracking-URL QR (separate from the package label QR which encodes internal data)
  useEffect(() => {
    if (variant !== "card" || !url) { setQrDataUrl(""); return; }
    let cancelled = false;
    QRCode.toDataURL(url, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 160,
      color: { dark: "#0f172a", light: "#ffffff" },
    })
      .then((d) => { if (!cancelled) setQrDataUrl(d); })
      .catch(() => { if (!cancelled) setQrDataUrl(""); });
    return () => { cancelled = true; };
  }, [url, variant]);

  if (!qrCodeId) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Tracking link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers / insecure contexts
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        toast.success("Tracking link copied");
        setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.error("Could not copy link");
      } finally {
        document.body.removeChild(ta);
      }
    }
  };

  const nativeShare = async () => {
    try {
      await navigator.share({
        title: "Track your delivery",
        text: `Track your package ${qrCodeId} on DeliveryBoss`,
        url,
      });
    } catch (e) {
      // User-cancelled share is silent. Any other error -> fallback to copy.
      if (e?.name !== "AbortError") copy();
    }
  };

  const openLink = () => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const downloadQR = async () => {
    try {
      // 1. Render the QR onto an offscreen image at high resolution
      const qrPng = await QRCode.toDataURL(url, {
        errorCorrectionLevel: "M",
        margin: 1,
        width: 512,
        color: { dark: "#0f172a", light: "#ffffff" },
      });
      const qrImg = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = qrPng;
      });

      // 2. Compose a branded canvas: header (DeliveryBoss) → QR → caption (Track {id})
      const W = 640;
      const H = 760;
      const canvas = document.createElement("canvas");
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext("2d");

      // background
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, W, H);

      // top brand bar
      ctx.fillStyle = "#0f172a";
      ctx.fillRect(0, 0, W, 80);
      ctx.fillStyle = "#22d3ee"; // cyan-400 accent
      ctx.fillRect(0, 80, W, 4);

      // brand text
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = "700 30px Inter, system-ui, sans-serif";
      ctx.fillText("DeliveryBoss", W / 2, 42);

      // QR centered
      const qrSize = 512;
      const qrX = (W - qrSize) / 2;
      const qrY = 120;
      ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

      // caption — small grey label + bold tracking id
      ctx.fillStyle = "#64748b"; // slate-500
      ctx.font = "500 16px ui-monospace, Menlo, monospace";
      ctx.fillText("SCAN TO TRACK", W / 2, qrY + qrSize + 28);

      ctx.fillStyle = "#0f172a";
      ctx.font = "700 26px ui-monospace, Menlo, monospace";
      ctx.fillText(qrCodeId, W / 2, qrY + qrSize + 64);

      // 3. Export
      const dataUrl = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `tracking-${qrCodeId}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("QR downloaded");
    } catch {
      toast.error("Could not download QR");
    }
  };

  // ---- Variant: icon (single compact button) ----
  if (variant === "icon") {
    return (
      <Button
        data-testid={`share-link-icon-${qrCodeId}`}
        size="sm"
        variant="ghost"
        title="Copy tracking link"
        onClick={copy}
        className="h-8 w-8 p-0 text-cyan-500 hover:text-cyan-300 hover:bg-cyan-500/10"
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
      </Button>
    );
  }

  // ---- Variant: inline (row of small buttons, no panel) ----
  if (variant === "inline") {
    return (
      <div data-testid={`share-link-inline-${qrCodeId}`} className="flex items-center gap-2">
        <Button
          data-testid={`share-copy-${qrCodeId}`}
          size="sm"
          variant="outline"
          onClick={copy}
          className="h-8 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800"
        >
          {copied ? <Check className="w-3.5 h-3.5 mr-1.5 text-green-400" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
          {copied ? "Copied" : "Copy link"}
        </Button>
        {canNativeShare && (
          <Button
            data-testid={`share-native-${qrCodeId}`}
            size="sm"
            variant="outline"
            onClick={nativeShare}
            className="h-8 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800"
          >
            <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share
          </Button>
        )}
        <Button
          data-testid={`share-open-${qrCodeId}`}
          size="sm"
          variant="ghost"
          onClick={openLink}
          className="h-8 text-slate-500 hover:text-cyan-400"
          title="Open tracking page"
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </Button>
      </div>
    );
  }

  // ---- Variant: card (default — full panel with QR preview + URL) ----
  return (
    <section
      data-testid={`share-link-card-${qrCodeId}`}
      className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 bg-slate-100/40 dark:bg-slate-900/40"
    >
      <div className="flex items-center gap-2 mb-3 text-cyan-400">
        <Share2 className="w-4 h-4" />
        <div className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-slate-500">
          {label || "Share Tracking Link"}
        </div>
      </div>
      <p className="text-xs text-slate-600 dark:text-slate-400 mb-3 leading-relaxed">
        Show the QR to your buyer to scan, or send them the link. Either way they see live status with no private details exposed.
      </p>

      <div className="flex gap-3 mb-3">
        <div
          data-testid={`share-link-qr-${qrCodeId}`}
          className="shrink-0 bg-white p-1.5 rounded-md border border-slate-200 dark:border-slate-700 self-start"
          title="Scan to track"
        >
          {qrDataUrl ? (
            <img src={qrDataUrl} alt={`Tracking QR for ${qrCodeId}`} className="w-20 h-20 block" />
          ) : (
            <div className="w-20 h-20 grid place-items-center text-[9px] text-slate-400 font-mono-tech">…</div>
          )}
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <div className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500 mb-1">Tracking URL</div>
          <div
            data-testid={`share-link-url-${qrCodeId}`}
            className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800"
          >
            <Link2 className="w-3.5 h-3.5 shrink-0 text-slate-500" />
            <span className="font-mono-tech text-[11px] text-slate-900 dark:text-cyan-200 truncate flex-1" title={url}>
              {url}
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          data-testid={`share-copy-${qrCodeId}`}
          size="sm"
          onClick={copy}
          className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium"
        >
          {copied ? <Check className="w-3.5 h-3.5 mr-1.5" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
          {copied ? "Copied!" : "Copy link"}
        </Button>
        {canNativeShare && (
          <Button
            data-testid={`share-native-${qrCodeId}`}
            size="sm"
            variant="outline"
            onClick={nativeShare}
            className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800"
          >
            <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share…
          </Button>
        )}
        <Button
          data-testid={`share-open-${qrCodeId}`}
          size="sm"
          variant="ghost"
          onClick={openLink}
          className="text-slate-600 dark:text-slate-400 hover:text-cyan-400"
        >
          <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open
        </Button>
        <Button
          data-testid={`share-download-qr-${qrCodeId}`}
          size="sm"
          variant="outline"
          onClick={downloadQR}
          className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800"
        >
          <Download className="w-3.5 h-3.5 mr-1.5" /> Download QR
        </Button>
      </div>
    </section>
  );
}
